import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
} from './ui/Dialog';
import { useAccessibility, FontSizePreset } from '../contexts/AccessibilityContext';
import { cn } from '../lib/utils';

const FONT_SIZE_OPTIONS: { value: FontSizePreset; label: string; size: string }[] = [
  { value: 'small', label: 'A', size: 'text-sm' },
  { value: 'medium', label: 'A', size: 'text-base' },
  { value: 'large', label: 'A', size: 'text-lg' },
];

export const AccessibilityToolbar: React.FC = () => {
  const { t } = useTranslation();
  const { settings, updateSettings, resetSettings } = useAccessibility();
  const [open, setOpen] = React.useState(false);

  return (
    <>
      {/* Floating trigger button - fixed bottom right */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[90]',
          'max-md:!hidden', // Explicitly hide on mobile
          'md:flex', // Show on desktop only
          'w-12 h-12 md:w-14 md:h-14',
          'bg-accent-primary text-black',
          'rounded-full shadow-lg',
          'flex items-center justify-center',
          'hover:bg-accent-primary/90 hover:scale-105',
          'transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-primary',
          'border-2 border-white/20'
        )}
        aria-label={t('accessibility.openToolbar')}
        title={t('accessibility.openToolbar')}
      >
        <AccessibilityIcon className="w-6 h-6 md:w-7 md:h-7" />
      </button>

      {/* Accessibility settings dialog */}
      <Dialog open={open} onOpenChange={(isOpen) => setOpen(isOpen)}>
        <DialogContent size="sm" showCloseButton>
          <DialogHeader>
            <DialogTitle>{t('accessibility.title')}</DialogTitle>
            <DialogDescription>
              {t('accessibility.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-8">
              {/* Text Size Presets */}
              <div className="space-y-4">
                <span className="text-sm font-semibold text-text-primary uppercase tracking-wide">
                  {t('accessibility.textSize')}
                </span>
                <div className="grid grid-cols-3 gap-3">
                  {FONT_SIZE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updateSettings({ fontSize: option.value })}
                      className={cn(
                        'flex flex-col items-center justify-center py-4 px-3 rounded-xl border-2 transition-all duration-200',
                        'focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated',
                        'hover:scale-[1.02] active:scale-[0.98]',
                        settings.fontSize === option.value
                          ? 'bg-accent-primary/20 border-accent-primary text-accent-primary shadow-lg shadow-accent-primary/20'
                          : 'bg-surface-container/50 text-text-secondary border-border-subtle hover:bg-surface-container hover:text-text-primary hover:border-border-subtle'
                      )}
                      aria-pressed={settings.fontSize === option.value}
                      aria-label={`${t('accessibility.textSize')}: ${t(`accessibility.fontSize${option.value.charAt(0).toUpperCase() + option.value.slice(1)}`)}`}
                    >
                      <span className={cn(option.size, 'font-bold leading-none mb-1')}>A</span>
                      <span className="text-xs font-medium uppercase tracking-wide">{option.value}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-border-subtle" />

              {/* Display Settings Section */}
              <div className="space-y-5">
                <span className="text-sm font-semibold text-text-primary uppercase tracking-wide">
                  Display
                </span>

                {/* High Contrast Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="high-contrast-toggle"
                      className="font-medium text-text-primary"
                    >
                      {t('accessibility.highContrast')}
                    </label>
                    <span className="text-xs text-text-muted">Increase contrast for better visibility</span>
                  </div>
                  <button
                    type="button"
                    id="high-contrast-toggle"
                    onClick={() => updateSettings({ highContrast: !settings.highContrast })}
                    className={cn(
                      'relative inline-flex h-7 w-[52px] items-center rounded-full transition-all duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated',
                      'hover:opacity-90 active:scale-[0.95]',
                      settings.highContrast
                        ? 'bg-accent-primary shadow-lg shadow-accent-primary/30'
                        : 'bg-surface-container border-2 border-border-subtle'
                    )}
                    role="switch"
                    aria-checked={settings.highContrast}
                    aria-label={t('accessibility.highContrast')}
                  >
                    <span
                      className={cn(
                        'inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200',
                        settings.highContrast ? 'translate-x-[28px]' : 'translate-x-1'
                      )}
                    />
                  </button>
                </div>

                {/* Highlight Links Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="highlight-links-toggle"
                      className="font-medium text-text-primary"
                    >
                      {t('accessibility.highlightLinks')}
                    </label>
                    <span className="text-xs text-text-muted">Emphasize links for easier navigation</span>
                  </div>
                  <button
                    type="button"
                    id="highlight-links-toggle"
                    onClick={() => updateSettings({ highlightLinks: !settings.highlightLinks })}
                    className={cn(
                      'relative inline-flex h-7 w-[52px] items-center rounded-full transition-all duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated',
                      'hover:opacity-90 active:scale-[0.95]',
                      settings.highlightLinks
                        ? 'bg-accent-primary shadow-lg shadow-accent-primary/30'
                        : 'bg-surface-container border-2 border-border-subtle'
                    )}
                    role="switch"
                    aria-checked={settings.highlightLinks}
                    aria-label={t('accessibility.highlightLinks')}
                  >
                    <span
                      className={cn(
                        'inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200',
                        settings.highlightLinks ? 'translate-x-[28px]' : 'translate-x-1'
                      )}
                    />
                  </button>
                </div>

                {/* Reduce Motion Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="reduce-motion-toggle"
                      className="font-medium text-text-primary"
                    >
                      {t('accessibility.reduceMotion')}
                    </label>
                    <span className="text-xs text-text-muted">Minimize animations throughout</span>
                  </div>
                  <button
                    type="button"
                    id="reduce-motion-toggle"
                    onClick={() => updateSettings({ reduceMotion: !settings.reduceMotion })}
                    className={cn(
                      'relative inline-flex h-7 w-[52px] items-center rounded-full transition-all duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated',
                      'hover:opacity-90 active:scale-[0.95]',
                      settings.reduceMotion
                        ? 'bg-accent-primary shadow-lg shadow-accent-primary/30'
                        : 'bg-surface-container border-2 border-border-subtle'
                    )}
                    role="switch"
                    aria-checked={settings.reduceMotion}
                    aria-label={t('accessibility.reduceMotion')}
                  >
                    <span
                      className={cn(
                        'inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200',
                        settings.reduceMotion ? 'translate-x-[28px]' : 'translate-x-1'
                      )}
                    />
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-border-subtle" />

              {/* Reset Button */}
              <button
                type="button"
                onClick={resetSettings}
                className={cn(
                  'w-full py-3 px-4 rounded-xl font-medium',
                  'bg-surface-container/50 text-text-secondary',
                  'border border-border-subtle',
                  'hover:bg-surface-container hover:text-text-primary hover:border-border-subtle',
                  'transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated',
                  'min-h-[48px]',
                  'active:scale-[0.98]'
                )}
              >
                {t('accessibility.resetDefaults')}
              </button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
};
